import { ContractState, ContractViewModel, CostViewModel, ServiceKind } from '@ail/contracts-api'
import { DocumentKind } from '@ail/documents-api'
import ExcellentExport, { ConvertOptions } from 'excellentexport'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Redirect, useParams } from 'react-router-dom'
import { downloadFile } from '../../helpers/FileDownload'
import { useAuth } from '../../hooks/useAuth'
import { useSettings } from '../../hooks/useSettings'
import { useGetContractQuery, useGetCostsQuery } from '../../redux/api/contractsApi'
import { useGetAllCustomersQuery, useGetAllUnitsQuery } from '../../redux/api/customerApi'
import { useGetContractDocumentsQuery, useLazyDownloadDocumentQuery } from '../../redux/api/documentsApi'
import { ContractProduct, Contracts } from '../../utils/contracts'
import Alert from '../common/Alert/Alert'
import DateSlider from '../common/DateSlider/DateSlider'
import { Grid, GridColumn } from '../common/Grid/Grid'
import './ContractCostKindDetails.scss'
import { Matomo } from '../../helpers/Matomo'
import DataSecurityPopup from '../ContractDetails/Popups/DataSecurityPopup'
import { Loader } from '../common/Loader/Loader'

const documentKindIcons: { [key in DocumentKind]?: string } = {
  [DocumentKind.Contract]: 'icon-contract',
  [DocumentKind.Cost]: 'icon-doc-cost',
  [DocumentKind.OutgoingInvoice]: 'icon-doc-invoice',
  [DocumentKind.Purchase]: 'icon-doc-purchase',
  [DocumentKind.Registration]: 'icon-doc-registration',
}

export function ContractCostKindDetails() {
  const [costsInterval, setCostsInterval] = useState<{ from: Date; to: Date }>()
  const [costsIntervalBounds, setCostsIntervalBounds] = useState<{ from: Date; to: Date }>()
  const [currentContract, setCurrentContract] = useState<ContractViewModel>()
  const [currentCosts, setCurrentCosts] = useState<CostViewModel[]>()
  const [failedDocument, setFailedDocument] = useState<string>()
  const [pendingSecureExport, setPendingSecureExport] = useState<{
    fileName: string
    data: string[][]
    id: string
    kind: ServiceKind
  }>()
  const [windowHeight, setWindowHeight] = useState<number>(0)
  const params = useParams() as { id: string; kind: ServiceKind }
  const { user } = useAuth()
  const { t } = useTranslation('common')

  const [contractCostsInterval, saveContractCostsInterval] = useSettings('contractCostsInterval', {
    remote: false,
  })
  const [excludeRestrictedDocuments, saveExcludeRestrictedDocuments] = useSettings('excludeRestrictedDocuments')
  const excludeRestricted = excludeRestrictedDocuments === 'y' || !user?.isInternalUser

  const contract = useGetContractQuery(params.id)
  const customers = useGetAllCustomersQuery()
  const units = useGetAllUnitsQuery()
  const documents = useGetContractDocumentsQuery({ contractId: params.id, excludeRestricted })
  const [downloadDocument] = useLazyDownloadDocumentQuery()

  const costs = useGetCostsQuery({ contractId: params.id, kind: params.kind, excludeRestricted })

  if (currentContract !== contract.currentData) {
    setCurrentContract(contract.currentData)

    if (contract.currentData) {
      const contractStart = new Date(contract.currentData.startDate)
      const from =
        contractCostsInterval?.contractId === contract.currentData.id
          ? new Date(contractCostsInterval.from)
          : contractStart
      const to =
        contractCostsInterval?.contractId === contract.currentData.id ? new Date(contractCostsInterval.to) : new Date()

      setCostsInterval({ from, to })
      setCostsIntervalBounds({ from: contractStart, to: new Date() })
    }
  }

  if (currentCosts !== costs.currentData) {
    setCurrentCosts(costs.currentData)
  }

  const contractCustomer = useMemo(() => {
    if (!contract.currentData || !customers.currentData) {
      return undefined
    }
    return customers.currentData.find((c) => c.value && contract.currentData?.owner?.startsWith(c.value))
  }, [contract.currentData, customers.currentData])

  const contractUnit = useMemo(() => {
    if (!contract.currentData || !units.currentData) {
      return undefined
    }
    return units.currentData.find((u) => u.value && contract.currentData?.owner === u.value)
  }, [contract.currentData, units.currentData])

  const filteredCosts = useMemo(() => {
    if (!costsInterval) {
      return []
    }

    return (
      currentCosts?.filter((c) => {
        const date = new Date(c.date)
        date.setDate(1)
        date.setHours(0, 0, 0, 0)
        return costsInterval.from <= date && date <= costsInterval.to
      }) ?? []
    )
  }, [currentCosts, costsInterval])

  const loading =
    contract.isFetching || customers.isFetching || units.isFetching || costs.isFetching || documents.isFetching

  useLayoutEffect(() => {
    function updateSize() {
      setWindowHeight(window.innerHeight)
    }
    window.addEventListener('resize', updateSize)
    updateSize()
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const downloadInvoice = useCallback(
    async (documentId: string, filename: string) => {
      try {
        const document = await downloadDocument({ documentId }).unwrap()
        const fileURL = URL.createObjectURL(document.data)
        downloadFile({
          fileURL,
          filename,
        })
      } catch {
        setFailedDocument(filename)
      }
    },
    [downloadDocument]
  )

  const renderInvoiceCell = useCallback(
    (item: CostViewModel) => {
      if (!item.invoiceNumber) {
        return <></>
      }

      const document = documents.currentData?.find((d) => {
        return (
          (typeof d.IncomingCostStateId === 'string' || typeof d.IncomingCostStateId === 'number') &&
          d.IncomingCostStateId.toString() === item.invoiceNumber
        )
      })

      if (!document) {
        return item.invoiceNumber
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <a onClick={() => downloadInvoice(document.id, document.fileName)}>{item.invoiceNumber}</a>
          <i className={documentKindIcons[document.kind]} />
        </div>
      )
    },
    [documents.currentData, downloadInvoice]
  )

  const exportData = useCallback((fileName: string, data: string[][]) => {
    const options: ConvertOptions = {
      openAsDownload: true,
      format: 'xlsx',
      filename: fileName,
    }

    const sheet = {
      name: '1',
      from: {
        array: data,
        arrayHasHeader: true,
      },
    }
    ExcellentExport.convert(options, [sheet])
  }, [])

  const handleExport = useCallback(
    (data: string[][]) => {
      const dateRange = costsInterval
        ? `-FROM-${costsInterval.from.toISOString().slice(0, 7)}-TO-${costsInterval.to.toISOString().slice(0, 7)}`
        : ''
      const fileName = `${params.id}-Cost-Detail-${params.kind}${dateRange}`
      setPendingSecureExport({ fileName, data, id: params.id, kind: params.kind })
      return undefined
    },
    [costsInterval, params.id, params.kind]
  )

  const exportSecureData = useCallback(() => {
    if (!pendingSecureExport) {
      return
    }
    Matomo.pushEvent('DataProtection', 'Export', 'ContractCosts', window.location.href)
    exportData(pendingSecureExport.fileName, pendingSecureExport.data)
    setPendingSecureExport(undefined)
  }, [exportData, pendingSecureExport])

  const columns = useMemo(() => {
    const result: GridColumn<CostViewModel>[] = [
      {
        key: 'date',
        name: t('DATE'),
        width: 130,
        renderCell: (data) => data.row.date.slice(0, 10),
        exportFormat: (item) => item.date.slice(0, 10),
        sortBy: (item) => new Date(item.date),
        filter: {
          type: 'inline_text_filter',
          filterFn: (item, value) => item.date.slice(0, 10).includes(value),
        },
      },
      {
        key: 'description',
        name: t('DESCRIPTION'),
        minWidth: 140,
        maxWidth: 1200,
        renderCell: (data) => <span title={data.row.description}>{data.row.description}</span>,
        exportFormat: (item) => item.description,
        sortBy: (item) => item.description,
        filter: {
          type: 'inline_text_filter',
          filterFn: (item, value) => item.description?.toLowerCase().includes(value.toLowerCase()),
        },
        remove: params.kind === ServiceKind.Fuel && excludeRestricted,
      },
      {
        key: 'supplierName',
        name: t('SUPPLIER_NAME'),
        minWidth: 160,
        maxWidth: 1200,
        renderCell: (data) => data.row.supplierName,
        exportFormat: (item) => item.supplierName,
        sortBy: (item) => item.supplierName,
        filter: {
          type: 'inline_text_filter',
          filterFn: (item, value) => item.supplierName?.toLowerCase().includes(value.toLowerCase()),
        },
      },
      {
        key: 'amount',
        name: t('AMOUNT'),
        width: 130,
        renderCell: (data) => data.row.amount,
        exportFormat: (item) => item.amount.toString(),
        sortBy: (item) => item.amount,
      },
      {
        key: 'documentNumber',
        name: t('DOCUMENT_NUMBER'),
        width: 160,
        renderCell: (data) => data.row.documentNr ?? data.row.DocumentNr,
        exportFormat: (item) => item.documentNr ?? item.DocumentNr,
        remove: params.kind !== ServiceKind.Kasko,
      },
      {
        key: 'shortDescription',
        name: t('SHORT_DESCRIPTION'),
        width: 160,
        renderCell: (data) => data.row.shortDescription ?? data.row.ShortDescription,
        exportFormat: (item) => item.shortDescription ?? item.ShortDescription,
        remove: ![ServiceKind.Tires, ServiceKind.Maintenance, ServiceKind.Rmt].includes(params.kind),
      },
      {
        key: 'actionType',
        name: t('ACTION'),
        width: 160,
        renderCell: (data) => data.row.actionType ?? data.row.ActionType,
        exportFormat: (item) => item.actionType ?? item.ActionType,
        remove: ![ServiceKind.Tires, ServiceKind.Maintenance, ServiceKind.Rmt].includes(params.kind),
      },
      {
        key: 'nrOfTires',
        name: t('NUMBER_OF_TIRES'),
        width: 160,
        renderCell: (data) => data.row.NrOfTires,
        exportFormat: (item) => item.NrOfTires,
        remove: params.kind !== ServiceKind.Tires,
      },
      {
        key: 'code',
        name: t('CODE'),
        width: 130,
        renderCell: (data) => data.row.RepairCode,
        exportFormat: (item) => item.RepairCode,
        remove: ![ServiceKind.Tires, ServiceKind.Maintenance, ServiceKind.Rmt].includes(params.kind),
      },
      {
        key: 'invoiceNumber',
        name: t('INVOICE_NUMBER'),
        width: 160,
        renderCell: (data) => renderInvoiceCell(data.row),
        exportFormat: (item) => item.invoiceNumber,
        sortBy: (item) => item.invoiceNumber,
        filter: {
          type: 'inline_text_filter',
          filterFn: (item, value) => item.invoiceNumber?.toLowerCase().includes(value.toLowerCase()),
        },
        remove:
          ![ServiceKind.Tires, ServiceKind.Maintenance, ServiceKind.Rmt, ServiceKind.Fuel].includes(params.kind) ||
          (params.kind === ServiceKind.Fuel && excludeRestricted),
      },
      {
        key: 'distance',
        name: t('DISTANCE'),
        width: 130,
        renderCell: (data) => data.row.distance ?? data.row.Distance,
        exportFormat: (item) => item.distance ?? item.Distance,
        remove: ![ServiceKind.Tires, ServiceKind.Maintenance, ServiceKind.Rmt, ServiceKind.Fuel].includes(params.kind),
      },
      {
        key: 'fuelType',
        name: t('TYPE'),
        width: 160,
        renderCell: (data) => data.row.fuelType ?? data.row.FuelType,
        exportFormat: (item) => item.fuelType ?? item.FuelType,
        remove: params.kind !== ServiceKind.Fuel,
      },
      {
        key: 'fuelQuantity',
        name: t('QUANTITY'),
        width: 160,
        renderCell: (data) => data.row.fuelQuantity ?? data.row.FuelQuantity,
        exportFormat: (item) => item.fuelQuantity ?? item.FuelQuantity,
        remove: params.kind !== ServiceKind.Fuel,
      },
    ]
    return result
  }, [excludeRestricted, params.kind, renderInvoiceCell, t])
  const isFullClosed = useMemo(
    () => (contract.data ? Contracts.getContractProduct(contract.data) === ContractProduct.FullClosed : false),
    [contract]
  )
  const kindLower = params.kind?.toLowerCase()
  if (isFullClosed && (kindLower === 'rmt' || kindLower === 'tires') && !user?.isInternalUser) {
    return <Redirect to={'/contractNotFound'} />
  }

  return (
    <>
      <Loader blocking={loading} style={{ overflow: 'hidden' }}>
        <ul className={excludeRestricted ? 'breadcrumbs' : 'breadcrumbs eyeOk'}>
          <li>
            <NavLink
              to={`/${
                contract.currentData?.state === ContractState.Initialized ? 'orderAndDelivery/' : 'generalContracts/'
              }${contractCustomer?.customer?.id}`}
              exact={true}
            >
              <span>{contractCustomer?.customer?.name}</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to={`/${
                contract.currentData?.state === ContractState.Initialized ? 'orderAndDelivery/' : 'generalContracts/'
              }${contractCustomer?.customer?.id}`}
              exact={true}
            >
              <span>{contractUnit?.unit?.name || t('NO_COST_CENTER')}</span>
            </NavLink>
          </li>
          <li>
            <NavLink to={`/generalContracts/details/${params.id}`} exact={true}>
              <span>{contract.currentData?.reference}</span>
            </NavLink>
          </li>
          <li>
            <span>{`${t(params.kind.toUpperCase())} `}</span>
          </li>
          <li>
            <span>
              {(user?.isAdmin || user?.isInternalUser) && (
                <i
                  className={excludeRestricted ? 'icon-hide' : 'icon-show'}
                  onClick={() => saveExcludeRestrictedDocuments(excludeRestricted ? 'n' : 'y')}
                />
              )}{' '}
            </span>
          </li>
        </ul>
        <div className="row">
          <div className="col-12">
            <div className="detailContentArea">
              <div className={`box-title ${excludeRestricted ? '' : 'eyeOk'}`}>
                <span>
                  <i className="icon-cost" /> <span>{t('COSTS')}</span>
                </span>
              </div>
              <div className="docDetails contrDetails">
                <div className="detail-container">
                  {!!costsInterval && (
                    <div className="detail-container__slider">
                      <DateSlider
                        onUpdate={({ from, to }) => {
                          setCostsInterval({ from, to })
                          saveContractCostsInterval({
                            from: from.toJSON(),
                            to: to.toJSON(),
                            contractId: params.id,
                          })
                        }}
                        includeLastYear={true}
                        startDate={costsInterval.from}
                        endDate={costsInterval.to}
                        minDateInSlider={costsIntervalBounds?.from}
                        maxDateInSLider={costsIntervalBounds?.to}
                        wholeRangeDrag={true}
                      />
                    </div>
                  )}
                  {
                    <div className="detail-container__grid kindDetail">
                      <div className="kindDetailParent" style={{ height: `${windowHeight - 356}px` }}>
                        <Grid
                          columns={columns}
                          rows={filteredCosts}
                          rowKeyGetter={(row) => `${row.amount} ${row.date} ${row.description}`}
                          onExport={handleExport}
                        />
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </Loader>

      <Alert
        onClose={() => setFailedDocument(undefined)}
        visible={!!failedDocument}
        title={t('ERROR')}
        message={`${t('DOWNLOAD_OF_DOCUMENT')} ${failedDocument} ${t('FAILED')}...`}
      />
      <DataSecurityPopup
        onConfirm={exportSecureData}
        onClose={() => setPendingSecureExport(undefined)}
        visible={!!pendingSecureExport}
      />
    </>
  )
}
